import React, { useMemo } from 'react';
import { createStackNavigator } from '@react-navigation/stack';
import { useTheme, ThemeProvider } from '@react-navigation/native';
import { useAppSelector } from '@/store';

// Wrapped screens with new Figma header navigation
import { ResidentPaymentsScreen } from '@/screens/residents/payments/ResidentPaymentsScreen';
import { ResidentProfileScreen } from '@/screens/residents/profile/ResidentProfileScreen';
import { MessagesStackNavigator } from './MessagesStackNavigator';
import { ResidentLinkedAccountsScreen } from '@/screens/residents/linkedAccounts/ResidentLinkedAccountsScreen';
import { ResidentEventsScreen } from '@/screens/residents/events/ResidentEventsScreen';
import { ResidentNotificationsScreen } from '@/screens/residents/notifications/ResidentNotificationsScreen';
import { CommunityMarketplaceScreen } from '@/screens/residents/marketplace/CommunityMarketplaceScreen';
import { ResidentFAQScreen } from '@/screens/residents/faq/ResidentFAQScreen';
import { MobileAccountScreen } from '@/screens/residents/account/mobile/MobileAccountScreen';
import { MobileResourcesScreen } from '@/screens/residents/resources/mobile/MobileResourcesScreen';
import { MobileMaintenanceScreen } from '@/screens/residents/maintenance/mobile/MobileMaintenanceScreen';
import { ResidentHomeScreen } from '@/screens/residents/home/ResidentHomeScreen';
import { CommunityBoardScreen } from '@/screens/residents/corkBoard/CommunityBoardScreen';
import { SurveysListScreen } from '@/screens/residents/corkBoard/SurveysListScreen';
import { PlannerScreen } from '@/screens/residents/planner/PlannerScreen';

export type ResidentMobileParamList = {
  ResidentHome: undefined;
  ResidentPlanner: { showBackLink?: boolean } | undefined;
  ResidentPayments: undefined;
  ResidentLinkedAccounts: undefined;
  ResidentMaintenance: undefined;
  ResidentEvents: undefined;
  ResidentMarketplace: undefined;
  ResidentCommunityBoard: undefined;
  ResidentSurveys: { showBackLink?: boolean } | undefined;
  ResidentResources: undefined;
  ResidentWifi: undefined;
  ResidentFaq: undefined;
  ResidentMessages: undefined;
  ResidentNotifications: undefined;
  ResidentProfile: undefined;
  ResidentAccountSettings: { section?: string } | undefined;
};

const Stack = createStackNavigator<ResidentMobileParamList>();

export const ResidentMobileNavigator: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const parentTheme = useTheme();

  // Override theme background to transparent so GradientBackground from layout shows through
  const transparentTheme = useMemo(() => ({
    ...parentTheme,
    colors: {
      ...parentTheme.colors,
      background: 'transparent',
    },
  }), [parentTheme]);

  // Hide Payments for Non-Leasing Occupants
  const shouldShowPayments = user?.customerType !== 'non-leasing occupant';

  return (
    <ThemeProvider value={transparentTheme}>
    <Stack.Navigator
      initialRouteName="ResidentHome"
      screenOptions={{
        headerShown: false,
        animationEnabled: false,
        cardStyle: { backgroundColor: 'transparent' },
      }}
    >
      <Stack.Screen name="ResidentHome" component={ResidentHomeScreen} />
      <Stack.Screen name="ResidentPlanner" component={PlannerScreen} />
      {shouldShowPayments && (
        <Stack.Screen name="ResidentPayments" component={ResidentPaymentsScreen} />
      )}
      <Stack.Screen name="ResidentLinkedAccounts" component={ResidentLinkedAccountsScreen} />
      <Stack.Screen name="ResidentMaintenance" component={MobileMaintenanceScreen} />
      <Stack.Screen name="ResidentEvents" component={ResidentEventsScreen} />
      <Stack.Screen name="ResidentMarketplace" component={CommunityMarketplaceScreen} />
      <Stack.Screen name="ResidentCommunityBoard" component={CommunityBoardScreen} />
      <Stack.Screen name="ResidentSurveys" component={SurveysListScreen} />
      <Stack.Screen name="ResidentResources" component={MobileResourcesScreen} />
      <Stack.Screen name="ResidentFaq" component={ResidentFAQScreen} />
      <Stack.Screen name="ResidentMessages" component={MessagesStackNavigator} />
      <Stack.Screen name="ResidentNotifications" component={ResidentNotificationsScreen} />
      <Stack.Screen name="ResidentProfile" component={ResidentProfileScreen} />
      <Stack.Screen name="ResidentAccountSettings" component={MobileAccountScreen} />
    </Stack.Navigator>
    </ThemeProvider>
  );
};
